import { GAME_TICKER_INTERVAL } from "@/config/constants";
import {
	CollectableType,
	getEventCollectableConfig,
	isCollectableEvent,
	useEventStore,
} from "@/event";
import type { EventCollectableConfig } from "@/event/integration/game/eventCollectableConfig";
import { tilerepoSettlementCastleBuildingsFrameNames } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import { settlementNames } from "@/game/entities/config/settlements";
import { TileVisibilityType } from "@/game/entities/config/terrain";
import type TileRepository from "@/game/TileRepository";
import useEmitter, { EmitterEvents, TileLabelAction } from "@/hooks/useEmitter";
import useResourcesStore from "@/store/useResourcesStore";
import useUserStore from "@/store/useUserStore";
import type { ClaimablesCoordinates } from "@/types";
import {
	type SettlementItem,
	type TileCoordinates,
	TileEntityParam,
	TileLabelSkin,
} from "@/types/tile";
import { floorNumber } from "@/utils/number";

class EventRepository {
	private spawnedCollectablesCoordinates = new Map<
		number,
		{ coordinates: TileCoordinates; type: number; skin: TileLabelSkin }
	>();
	private tilerepo?: TileRepository;
	private initializedEventIds = new Set<string>();
	/** Remaining initial spawns, decremented only on success so claims cannot refill. */
	private remainingInitializationSpawns = new Map<string, number>();
	private backgroundSince: number | null = null;
	private pendingOfflinePeriods: { start: number; end: number }[] = [];
	private foregroundGeneration = 0;
	private coldStartOfflineApplied = false;

	public onBackground(): void {
		if (this.backgroundSince !== null || !this.tilerepo) return;
		this.backgroundSince = Date.now();
		this.foregroundGeneration++;
	}

	public onForeground(): number {
		if (this.backgroundSince !== null) {
			this.pendingOfflinePeriods.push({
				start: this.backgroundSince,
				end: Date.now(),
			});
			this.backgroundSince = null;
		}
		return ++this.foregroundGeneration;
	}

	/** Apply only after the foreground event overview has hydrated. */
	public applyForegroundCollectables(generation: number): void {
		if (
			generation !== this.foregroundGeneration ||
			this.backgroundSince !== null ||
			!this.tilerepo ||
			this.pendingOfflinePeriods.length === 0
		)
			return;

		this.spawnInitialCollectables();
		for (const { start, end } of this.pendingOfflinePeriods) {
			this.spawnOfflineCollectables(end - start, end);
		}
		this.pendingOfflinePeriods = [];
	}

	public init(tilerepo: TileRepository): void {
		this.tilerepo = tilerepo;
		this.spawnedCollectablesCoordinates.clear();
		const { mapClaimablesCoordinates } = useEventStore();
		mapClaimablesCoordinates.forEach(
			(coordinates: ClaimablesCoordinates): void => {
				const tileIndex = this.tilerepo!.getTileIndexByTileCoordinates(
					coordinates.coordinates,
				);
				const skin = this.getSkin(coordinates.type);
				this.spawnedCollectablesCoordinates.set(tileIndex, {
					coordinates: coordinates.coordinates,
					type: coordinates.type,
					skin,
				});
				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates: coordinates.coordinates,
					skin,
					action: TileLabelAction.CREATE,
				});
			},
		);

		this.spawnInitialCollectables();
		if (!this.coldStartOfflineApplied) {
			this.coldStartOfflineApplied = true;
			this.spawnOfflineCollectables(
				this.tilerepo.getOfflineTickCount() * GAME_TICKER_INTERVAL,
				Date.now(),
			);
		}
	}

	/** Removes event collectable labels and clears local/server-synced spawn state. */
	public clearSpawnedCollectablesFromMap(): void {
		this.initializedEventIds.clear();
		this.remainingInitializationSpawns.clear();
		if (this.spawnedCollectablesCoordinates.size > 0) {
			for (const {
				coordinates,
				skin,
			} of this.spawnedCollectablesCoordinates.values()) {
				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates,
					skin,
					action: TileLabelAction.DELETE,
				});
			}
		}

		this.spawnedCollectablesCoordinates.clear();
		useEventStore().setTileClaimablesCoordinates([]);
	}

	public onTick(): void {
		if (
			this.backgroundSince !== null ||
			this.pendingOfflinePeriods.length > 0
		)
			return;
		const { isActive, attributes } = useEventStore();
		if (!isActive || !isCollectableEvent(attributes.type)) {
			this.clearSpawnedCollectablesFromMap();
			return;
		}

		this.spawnInitialCollectables();
		this.trySpawnCollectable();
	}

	public getPassiveEventium(): number {
		const { isActive, attributes } = useEventStore();
		if (!isActive || !isCollectableEvent(attributes.type)) return 0;
		return getEventCollectableConfig(attributes.type).getPassiveIncome();
	}

	public trySpawnCollectable(): void {
		const { isActive, attributes } = useEventStore();
		if (!isActive || !isCollectableEvent(attributes.type)) return;
		const config = getEventCollectableConfig(attributes.type);
		if (
			Math.random() >= config.spawnChancePerTick ||
			this.spawnedCollectablesCoordinates.size >= config.getPickupCap() ||
			this.tilerepo === undefined
		)
			return;

		this.spawnCollectable(config);
	}

	private spawnInitialCollectables(): void {
		const eventStore = useEventStore();
		const eventType = eventStore.attributes.type;
		const eventId = eventStore.id;
		if (
			!eventStore.isActive ||
			!eventId ||
			!isCollectableEvent(eventType) ||
			!this.tilerepo ||
			this.initializedEventIds.has(eventId)
		)
			return;
		const config = getEventCollectableConfig(eventType);
		const hasCollectedAny = Object.values(eventStore.collectedByType).some(
			(count: number): boolean => count > 0,
		);
		const initialCount = hasCollectedAny
			? 0
			: Math.max(
					config.initialPickupCount -
						this.spawnedCollectablesCoordinates.size,
					0,
				);
		let remaining =
			this.remainingInitializationSpawns.get(eventId) ?? initialCount;
		remaining = Math.max(
			0,
			Math.min(
				remaining,
				config.getPickupCap() -
					this.spawnedCollectablesCoordinates.size,
			),
		);

		while (remaining > 0) {
			if (!this.spawnCollectable(config)) {
				this.remainingInitializationSpawns.set(eventId, remaining);
				return;
			}
			remaining--;
		}

		this.initializedEventIds.add(eventId);
		this.remainingInitializationSpawns.delete(eventId);
	}

	private spawnOfflineCollectables(durationMs: number, end: number): void {
		const { isActive, attributes, startDate } = useEventStore();
		if (!isActive || !isCollectableEvent(attributes.type) || !startDate)
			return;
		const config = getEventCollectableConfig(attributes.type);
		if (!config.offlinePickupIntervalSeconds) return;
		const eligibleMs = Math.max(
			0,
			Math.min(
				durationMs,
				useUserStore().maxOfflineTime,
				end - startDate.getTime(),
			),
		);
		const count = Math.min(
			Math.floor(
				eligibleMs / (config.offlinePickupIntervalSeconds * 1_000),
			),
			config.getPickupCap() - this.spawnedCollectablesCoordinates.size,
		);
		for (let i = 0; i < count; i++) {
			if (!this.spawnCollectable(config)) break;
		}
	}

	private spawnCollectable(config: EventCollectableConfig): boolean {
		if (
			!this.tilerepo ||
			this.spawnedCollectablesCoordinates.size >= config.getPickupCap()
		)
			return false;

		const coordinates = this.findValidCoordinates();
		if (coordinates === null) return false;

		const type =
			Math.random() < config.getRareChance()
				? CollectableType.RARE
				: CollectableType.COMMON;
		const skin = config.getSkin(type);
		const tileIndex =
			this.tilerepo.getTileIndexByTileCoordinates(coordinates);
		this.spawnedCollectablesCoordinates.set(tileIndex, {
			coordinates,
			type,
			skin,
		});
		useEventStore().setTileClaimablesCoordinates(
			Array.from(this.spawnedCollectablesCoordinates.values()).map(
				(data: ClaimablesCoordinates): ClaimablesCoordinates => ({
					type: data.type,
					coordinates: data.coordinates,
				}),
			),
		);
		useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
			coordinates,
			skin,
			action: TileLabelAction.CREATE,
		});
		return true;
	}
	private findValidCoordinates(): TileCoordinates | null {
		const mainSettlement: SettlementItem | undefined =
			settlementNames.get(1);
		if (!mainSettlement) return null;
		const centerX = mainSettlement.x;
		const centerY = mainSettlement.y;
		const eventType = useEventStore().attributes.type;
		if (!isCollectableEvent(eventType)) return null;
		const config = getEventCollectableConfig(eventType);
		const maxSpawnDistance = config.getMaxSpawnDistance();

		for (let attempt = 0; attempt < 100; attempt++) {
			const coords = this.getNewRandomCoordinates(
				centerX,
				centerY,
				maxSpawnDistance - attempt * 2,
			);
			if (this.isValidSpawnLocation(coords)) {
				return coords;
			}
		}

		return null;
	}

	private getNewRandomCoordinates(
		centerX: number,
		centerY: number,
		maxDistance: number,
	): TileCoordinates {
		//generate xR randomly between -maxDistance and maxDistance
		const xR =
			floorNumber(Math.random() * (maxDistance * 2 + 1)) - maxDistance;
		// generate yR based on xR to ensure the coordination sum is within the maxDistance
		const yMax = maxDistance - Math.abs(xR);
		const yR = floorNumber(Math.random() * (yMax * 2 + 1)) - yMax;
		return {
			x: centerX + xR,
			y: centerY + yR,
		};
	}

	private isValidSpawnLocation(coords: TileCoordinates): boolean {
		const tileIndex = this.tilerepo!.getTileIndexByTileCoordinates(coords);
		if (
			this.tilerepo === undefined ||
			this.spawnedCollectablesCoordinates.has(tileIndex)
		)
			return false;

		const tile = this.tilerepo?.getTileEntityByTileCoordinates(coords);
		if (!tile) return false;
		const visibility = this.tilerepo.getParam(
			tile.index,
			TileEntityParam.VISIBILITY,
		);
		return !(
			visibility !== TileVisibilityType.EXPLORED ||
			tilerepoSettlementCastleBuildingsFrameNames.has(tile.frameName)
		);
	}

	public claimCollectable(coords: TileCoordinates): void {
		const tileIndex = this.tilerepo!.getTileIndexByTileCoordinates(coords);
		const collectableData =
			this.spawnedCollectablesCoordinates.get(tileIndex);
		if (!collectableData) return;
		const eventStore = useEventStore();
		const eventType = eventStore.attributes.type;
		if (!isCollectableEvent(eventType)) return;
		const config = getEventCollectableConfig(eventType);
		const eventiumAmount = config.getReward(collectableData.type);
		const eventiumRewardKey = `${eventType}-eventium`;
		const reward = {
			[eventiumRewardKey]: eventiumAmount,
		};
		useResourcesStore().increase(
			ResourceType.EVENTIUM,
			eventiumAmount,
			"eventCollectableClaim",
		);
		eventStore.incrementCollectedByType(collectableData.type, 1);
		setTimeout((): void => {
			useEmitter().emit(EmitterEvents.PhaserTileClick, {
				x: collectableData.coordinates.x,
				y: collectableData.coordinates.y,
				reward,
				randomX: false,
			});
		}, 100);

		this.spawnedCollectablesCoordinates.delete(tileIndex);
		eventStore.setTileClaimablesCoordinates(
			Array.from(this.spawnedCollectablesCoordinates.values()).map(
				(data: ClaimablesCoordinates): ClaimablesCoordinates => ({
					type: data.type,
					coordinates: data.coordinates,
				}),
			),
		);
		useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
			coordinates: coords,
			skin: collectableData.skin,
			action: TileLabelAction.DELETE,
		});
	}

	private getSkin(collectableType: CollectableType): TileLabelSkin {
		const eventType = useEventStore().attributes.type;
		if (!isCollectableEvent(eventType)) return TileLabelSkin.EASTER_EGG;
		return getEventCollectableConfig(eventType).getSkin(collectableType);
	}
}
export const eventRepository = new EventRepository();
